import { datadogLogs } from '@datadog/browser-logs'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode } from 'model/common'
import { AddTermsAndConditionsPayload } from 'model/newApiPayloads'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

const useSendTermsResponse = () => {
  const post = usePostCallbackWithRetry<AddTermsAndConditionsPayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress],
    addResponseChoicePromise
  } = useContext<AppState>(AppStateContext)

  const sendTermsResponse = async ({
    entryPosition,
    contextPosition,
    renderedContextPosition
  }: {
    entryPosition: number
    contextPosition: number
    renderedContextPosition: number
  }) => {
    const payload: AddTermsAndConditionsPayload = {
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }
    const url = coreRespondentUrl(
      `/surveys/${surveyId}/responses/terms_and_conditions`
    )

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Terms and conditions response was sent', {
          questionTypeCode: 'terms_and_conditions',
          surveyId,
          respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW,
          requestDurationMs: Math.round(requestDuration),
          payloadSize: JSON.stringify(payload).length
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.error(
          'Network Failure: Terms and conditions response failed to be sent',
          {
            err,
            httpStatusCode: err?.response?.status ?? null,
            questionTypeCode: 'terms_and_conditions',
            surveyId,
            respondentId,
            apiUsed: API_DISTRIBUTION_FACET.NEW,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
        } else {
          throw new Error(
            'Could not find respondent progress in useSendTermsResponse'
          )
        }
      })

    addResponseChoicePromise(promise)

    return promise
  }

  return { sendTermsResponse }
}

export default useSendTermsResponse
